/**
 * Normalizes job location fields for job detail UI and SEO helpers:
 * - Expands composite strings (e.g. "A; B") into separate entries
 * - Drops generic ATS placeholders like "Multiple locations" when other concrete places exist
 */

import { sortLocationsByMarketplacePriority } from '../../../../../utils/location/sort-locations-by-marketplace-priority';

function normalizeToRawArray(location: string | string[] | undefined | null): string[] {
  if (Array.isArray(location)) {
    return location.filter((loc): loc is string => typeof loc === 'string' && loc.trim() !== '');
  }
  if (typeof location === 'string' && location.trim() !== '') {
    return [location];
  }
  return [];
}

/**
 * True for vague multi-location labels from ATS feeds (not real geography).
 * When at least one other concrete location exists, these should be dropped from display.
 */
export function isGenericMultipleLocationsLabel(value: string): boolean {
  const t = value.trim().toLowerCase().replace(/\s+/g, ' ');
  const variants = [
    /^multiple\s+locations(\s*,?\s*finland)?$/,
    /^various\s+locations(\s*,?\s*finland)?$/,
    /^several\s+locations(\s*,?\s*finland)?$/,
    /^useita\s+sijainteja(\s*,?\s*suomessa)?$/,
    /^flera\s+platser(\s*,?\s*i\s+finland)?$/,
    /^flera\s+orter(\s*,?\s*i\s+finland)?$/,
  ];
  return variants.some((re) => re.test(t));
}

function splitCompositeEntry(entry: string): string[] {
  const t = entry.trim();
  if (!t) return [];

  const bySemi = t.split(';').map((s) => s.trim()).filter(Boolean);
  if (bySemi.length > 1) return bySemi;

  const byPipe = t.split('|').map((s) => s.trim()).filter(Boolean);
  if (byPipe.length > 1) return byPipe;

  const bySlash = t.split(/\s+\/\s+/).map((s) => s.trim()).filter(Boolean);
  if (bySlash.length > 1) return bySlash;

  return [t];
}

function dedupeCaseInsensitive(locations: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const loc of locations) {
    const trimmed = loc.trim();
    const k = trimmed.toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(trimmed);
  }
  return out;
}

/**
 * All distinct location segments for display / prioritization (expanded + deduped).
 */
export function collectJobDetailLocationParts(location: string | string[] | undefined | null): string[] {
  const base = normalizeToRawArray(location);
  const expanded = base.flatMap(splitCompositeEntry);
  return dedupeCaseInsensitive(expanded);
}

/**
 * Same as {@link collectJobDetailLocationParts}, then removes generic multi-location labels
 * when there is more than one segment so real cities are not hidden behind "Multiple locations".
 */
export function getLocationsForJobDetailDisplay(location: string | string[] | undefined | null): string[] {
  let parts = collectJobDetailLocationParts(location);
  if (parts.length > 1) {
    parts = parts.filter((p) => !isGenericMultipleLocationsLabel(p));
  }
  return parts;
}

/** Single line for headers / meta (e.g. "Helsinki · Tampere · Remote"). */
export function formatJobDetailLocationDisplay(location: string | string[] | undefined | null): string {
  const parts = sortLocationsByMarketplacePriority(getLocationsForJobDetailDisplay(location));
  if (parts.length === 0) return '';
  return parts.join(' · ');
}

/**
 * Location segment for the job detail page H1 only. When there are multiple distinct
 * locations, returns an empty string so the title stays title · company without a long place list.
 */
export function formatLocationForJobHeaderTitle(location: string | string[] | undefined | null): string {
  const parts = getLocationsForJobDetailDisplay(location);
  if (parts.length > 1) return '';
  return formatJobDetailLocationDisplay(location);
}

/** First non-generic segment — useful for SEO slug and similar-job primary facet. */
export function getFirstConcreteLocationSegment(location: string | string[] | undefined | null): string {
  const parts = sortLocationsByMarketplacePriority(collectJobDetailLocationParts(location));
  const concrete = parts.filter((p) => !isGenericMultipleLocationsLabel(p));
  return (concrete[0] ?? parts[0] ?? '').trim();
}
